import { each, isPlainObject } from '@art-suite/art-core-ts';
import { getEffectiveScanValue, ScanAudio, ScanPhoto, ScanVideo } from '../ScanRoot';
import { partIsApplicable } from './ScanOptimizedGetters';
import { AudioIdUsesByAudioId, PhotoIdUsesByPhotoId, ReducedValues, ScanPartOptimized } from './ScanRootOptimizedSchemas';

// **************************************************************
// OptimizedSearchText generation
// **************************************************************
const pushIfDefined = (searchText: string[], value: string | undefined) => {
  if (value) searchText.push(value);
};
const pushArrayIfDefined = (searchText: string[], value: string[] | undefined) => {
  if (value) searchText.push(...value);
};

export const getOptimizedSearchText = <T extends ScanPartOptimized>(
  scanPart: T,
  newOptimizedChildren?: ScanPartOptimized[],
  originalScanPart?: ScanPartOptimized,
  originalOptimizedChildren?: ScanPartOptimized[],
): string => {
  if (
    originalScanPart
    && originalScanPart.customLabel === scanPart.customLabel
    && originalScanPart.skuDetails === scanPart.skuDetails
    && originalOptimizedChildren === newOptimizedChildren
  ) { // no change, return the same instance
    return originalScanPart.optimized.searchText; // no change, return the same instance
  }

  const { skuDetails, template } = scanPart;
  const searchText: string[] = [];
  pushIfDefined(searchText, scanPart.customLabel);
  pushIfDefined(searchText, template.name);
  pushIfDefined(searchText, template.description);
  if (skuDetails) {
    pushIfDefined(searchText, skuDetails.title);
    pushIfDefined(searchText, skuDetails.description);
    pushArrayIfDefined(searchText, skuDetails.skus);
    pushIfDefined(searchText, skuDetails.productSupplier);
    pushIfDefined(searchText, skuDetails.color);
    pushIfDefined(searchText, skuDetails.brand);
  }
  if (newOptimizedChildren) {
    for (const part of newOptimizedChildren) {
      pushIfDefined(searchText, part.optimized.searchText);
    }
  }
  return searchText.join('\n');
};

// **************************************************************
// Photos
// **************************************************************
const mergeInPhotoIdUsesByPhotoId = (intoPhotoIdUses: PhotoIdUsesByPhotoId, fromPhotoIdUses: PhotoIdUsesByPhotoId) => {
  each(fromPhotoIdUses, (photoIdUses, photoId) => {
    if (!intoPhotoIdUses[photoId]) intoPhotoIdUses[photoId] = [];
    intoPhotoIdUses[photoId].push(...photoIdUses);
  });
  return intoPhotoIdUses;
};

/**
 * assumes newOptimizedChildren already have their allPhotoIdUsesByPhotoId set
 * @param scanPart
 * @param groupId
 * @param taskId
 * @param optionId
 * @param lineItemId
 * @param newOptimizedChildren
 * @returns
 */
export const getAllPhotoIdUsesByPhotoId = (
  scanPart: ScanPartOptimized,
  idsByDepth: (string | undefined)[],
  newOptimizedChildren?: ScanPartOptimized[],
  originalScanPart?: ScanPartOptimized,
  originalOptimizedChildren?: ScanPartOptimized[],
): PhotoIdUsesByPhotoId => {
  if (originalScanPart && originalScanPart.photos === scanPart.photos && originalOptimizedChildren === newOptimizedChildren) {
    return originalScanPart.optimized.allPhotoIdUsesByPhotoId; // no change, return the same instance
  }

  const photoIdUses: PhotoIdUsesByPhotoId = {};

  // Add photo uses from the main scan part
  const [_scanId, groupId, taskId, optionId, lineItemId] = idsByDepth;
  const { scanPartType } = scanPart;

  if (scanPart.photos) {
    for (const photo of scanPart.photos) {
      const { photoId } = photo;
      if (!photoIdUses[photoId]) photoIdUses[photoId] = [];
      photoIdUses[photoId].push({ photoId, scanPartType, groupId, taskId, optionId, lineItemId });
    }
  }

  // Add photo uses from sub parts
  if (newOptimizedChildren) {
    for (const part of newOptimizedChildren) {
      if (partIsApplicable(part)) {
        mergeInPhotoIdUsesByPhotoId(photoIdUses, part.optimized.allPhotoIdUsesByPhotoId);
      }
    }
  }

  return photoIdUses;
};

/** @deprecated use getAllPhotoIdsFromAllPhotos instead */
export const getAllPhotoIds = (
  scanPart: ScanPartOptimized,
  newOptimizedChildren?: ScanPartOptimized[],
  originalScanPart?: ScanPartOptimized,
  originalOptimizedChildren?: ScanPartOptimized[],
): string[] => {
  if (originalScanPart && originalScanPart.photos === scanPart.photos && originalOptimizedChildren === newOptimizedChildren) {
    return originalScanPart.optimized.allPhotoIds; // no change, return the same instance
  }

  const uniquePhotosIds: Set<string> = new Set();
  if (scanPart.photos) {
    for (const photo of scanPart.photos) {
      uniquePhotosIds.add(photo.photoId);
    }
  }
  if (newOptimizedChildren) {
    for (const { optimized: { allPhotoIds } } of newOptimizedChildren) {
      for (const photoId of allPhotoIds) {
        uniquePhotosIds.add(photoId);
      }
    }
  }
  return Array.from(uniquePhotosIds);
};

export const getAllPhotoIdsFromAllPhotos = (newAllPhotos: ScanPhoto[], oldAllPhotos?: ScanPhoto[], oldAllPhotoIds?: string[]): string[] => {
  if (!newAllPhotos) return [];
  if (oldAllPhotoIds && oldAllPhotos === newAllPhotos) return oldAllPhotoIds;
  return newAllPhotos.map(photo => photo.photoId);
};

export const getAllPhotos = (
  scanPart: ScanPartOptimized,
  newOptimizedChildren?: ScanPartOptimized[],
  originalScanPart?: ScanPartOptimized,
  originalOptimizedChildren?: ScanPartOptimized[],
): ScanPhoto[] => {
  if (originalScanPart && originalScanPart.photos === scanPart.photos && originalOptimizedChildren === newOptimizedChildren) {
    return originalScanPart.optimized.allPhotos; // no change, return the same instance
  }

  const uniquePhotosIds: Set<string> = new Set();
  const photos: ScanPhoto[] = [];
  if (scanPart.photos) {
    for (const photo of scanPart.photos) {
      if (!uniquePhotosIds.has(photo.photoId)) {
        uniquePhotosIds.add(photo.photoId);
        photos.push(photo);
      }
    }
  }
  if (newOptimizedChildren) {
    for (const { optimized: { allPhotos } } of newOptimizedChildren) {
      for (const photo of allPhotos) {
        if (!uniquePhotosIds.has(photo.photoId)) {
          uniquePhotosIds.add(photo.photoId);
          photos.push(photo);
        }
      }
    }
  }
  const defaultDate = scanPart.createdAt ?? new Date().getTime();
  return photos.sort((a, b) => (a.addedAt ?? defaultDate) - (b.addedAt ?? defaultDate));
};

// **************************************************************
// Videos
// **************************************************************

export const getAllVideoIdsFromAllVideos = (newAllVideos: ScanVideo[], oldAllVideos?: ScanVideo[], oldAllVideoIds?: string[]): string[] => {
  if (!newAllVideos) return [];
  if (oldAllVideoIds && oldAllVideos === newAllVideos) return oldAllVideoIds;
  return newAllVideos.map(video => video.videoId);
};

/**
 * Scan video attachments on ScanRoot, ScanGroup, and ScanTask. Each entry may carry a
 * `videoOffset` describing the time range it covers into its source video (segment not yet
 * clipped = `videoId` points at the source; clip persisted = `videoId` is the clip row).
 * Rolls up like photos: self `videos[]` + descendants' `allVideos`.
 */
export const getAllVideos = (
  scanPart: ScanPartOptimized,
  newOptimizedChildren?: ScanPartOptimized[],
  originalScanPart?: ScanPartOptimized,
  originalOptimizedChildren?: ScanPartOptimized[],
): ScanVideo[] => {
  const localVideos = (scanPart as { videos?: ScanVideo[] }).videos;
  if (originalScanPart && (originalScanPart as { videos?: ScanVideo[] }).videos === localVideos && originalOptimizedChildren === newOptimizedChildren) {
    return originalScanPart.optimized.allVideos;
  }

  const uniqueVideoIds = new Set<string>();
  const videos: ScanVideo[] = [];
  if (localVideos) {
    for (const video of localVideos) {
      if (!uniqueVideoIds.has(video.videoId)) {
        uniqueVideoIds.add(video.videoId);
        videos.push(video);
      }
    }
  }
  if (newOptimizedChildren) {
    for (const { optimized: { allVideos } } of newOptimizedChildren) {
      for (const video of allVideos) {
        if (!uniqueVideoIds.has(video.videoId)) {
          uniqueVideoIds.add(video.videoId);
          videos.push(video);
        }
      }
    }
  }
  const defaultDate = scanPart.createdAt ?? new Date().getTime();
  return videos.sort((a, b) => (a.addedAt ?? defaultDate) - (b.addedAt ?? defaultDate));
};

// **************************************************************
// Audio
// **************************************************************

const mergeInAudioIdUsesByAudioId = (intoAudioIdUses: AudioIdUsesByAudioId, fromAudioIdUses: AudioIdUsesByAudioId) => {
  each(fromAudioIdUses, (audioIdUses, audioId) => {
    if (!intoAudioIdUses[audioId]) intoAudioIdUses[audioId] = [];
    intoAudioIdUses[audioId].push(...audioIdUses);
  });
  return intoAudioIdUses;
};

export const getAllAudioIdUsesByAudioId = (
  scanPart: ScanPartOptimized,
  idsByDepth: (string | undefined)[],
  newOptimizedChildren?: ScanPartOptimized[],
  originalScanPart?: ScanPartOptimized,
  originalOptimizedChildren?: ScanPartOptimized[],
): AudioIdUsesByAudioId => {
  const localAudio = (scanPart as { audio?: ScanAudio[] }).audio;
  if (originalScanPart && (originalScanPart as { audio?: ScanAudio[] }).audio === localAudio && originalOptimizedChildren === newOptimizedChildren) {
    return originalScanPart.optimized.allAudioIdUsesByAudioId;
  }

  const audioIdUses: AudioIdUsesByAudioId = {};
  const [_scanId, groupId, taskId, optionId, lineItemId] = idsByDepth;
  const { scanPartType } = scanPart;

  if (localAudio) {
    for (const audio of localAudio) {
      const { audioId } = audio;
      if (!audioIdUses[audioId]) audioIdUses[audioId] = [];
      audioIdUses[audioId].push({ audioId, scanPartType, groupId, taskId, optionId, lineItemId });
    }
  }

  if (newOptimizedChildren) {
    for (const part of newOptimizedChildren) {
      if (partIsApplicable(part)) {
        mergeInAudioIdUsesByAudioId(audioIdUses, part.optimized.allAudioIdUsesByAudioId);
      }
    }
  }

  return audioIdUses;
};

export const getAllAudioIdsFromAllAudio = (newAllAudio: ScanAudio[], oldAllAudio?: ScanAudio[], oldAllAudioIds?: string[]): string[] => {
  if (!newAllAudio) return [];
  if (oldAllAudioIds && oldAllAudio === newAllAudio) return oldAllAudioIds;
  return newAllAudio.map(audio => audio.audioId);
};

/**
 * Scan audio attachments on ScanRoot, ScanGroup, and ScanTask. Each entry references a
 * ScanVideoAudio blob row and the walkthrough it is scoped to. Rolls up like videos:
 * self `audio[]` + descendants' `allAudio`.
 */
export const getAllAudio = (
  scanPart: ScanPartOptimized,
  newOptimizedChildren?: ScanPartOptimized[],
  originalScanPart?: ScanPartOptimized,
  originalOptimizedChildren?: ScanPartOptimized[],
): ScanAudio[] => {
  const localAudio = (scanPart as { audio?: ScanAudio[] }).audio;
  if (originalScanPart && (originalScanPart as { audio?: ScanAudio[] }).audio === localAudio && originalOptimizedChildren === newOptimizedChildren) {
    return originalScanPart.optimized.allAudio;
  }

  const uniqueAudioIds = new Set<string>();
  const audioEntries: ScanAudio[] = [];
  if (localAudio) {
    for (const audio of localAudio) {
      if (!uniqueAudioIds.has(audio.audioId)) {
        uniqueAudioIds.add(audio.audioId);
        audioEntries.push(audio);
      }
    }
  }
  if (newOptimizedChildren) {
    for (const { optimized: { allAudio } } of newOptimizedChildren) {
      for (const audio of allAudio) {
        if (!uniqueAudioIds.has(audio.audioId)) {
          uniqueAudioIds.add(audio.audioId);
          audioEntries.push(audio);
        }
      }
    }
  }
  const defaultDate = scanPart.createdAt ?? new Date().getTime();
  return audioEntries.sort((a, b) => (a.addedAt ?? defaultDate) - (b.addedAt ?? defaultDate));
};

// **************************************************************
// REDUCED VALUES FUNCTIONS
// **************************************************************

/**
 * Get the reduced values from the values and the sub parts
 *
 * Reduced Values: - e.g.
 * - photos: photos is handled special: if values of any of newOptimizedChildren[].photos are set, then the photos are the sum of the values of newOptimizedChildren[].photos else null
 * - plus all other named values are also summed up; materialCost and laborCost have special meaning to the UX and exports, but the scan-template system doesn't need to know about them
 *
 * @param values - The values to get the reduced values from
 * @param newOptimizedChildren - The sub parts to get the reduced values from
 * @returns The reduced values
 */
export const getReducedValues = (
  scanPart: ScanPartOptimized,
  newOptimizedChildren?: ScanPartOptimized[],
  originalScanPart?: ScanPartOptimized,
  originalOptimizedChildren?: ScanPartOptimized[],
): ReducedValues => {
  if (originalScanPart && originalScanPart.values === scanPart.values && originalOptimizedChildren === newOptimizedChildren) {
    return originalScanPart.optimized.reducedValues; // no change, return the same instance
  }

  const reducedValues: Record<string, number> = {};

  const addReducedValue = (value: any, key: string) => {
    if (isPlainObject(value)) value = getEffectiveScanValue(value);
    if (typeof value === 'number') reducedValues[key] = (reducedValues[key] ?? 0) + value;
  };

  // add own values
  each(scanPart.values, addReducedValue);

  // add reduced values from sub parts
  if (newOptimizedChildren) {
    for (const part of newOptimizedChildren) {
      if (partIsApplicable(part)) {
        each(part.optimized.reducedValues, addReducedValue);
      }
    }
  }

  return reducedValues;
};
